// eslint-disable-next-line object-curly-newline
import React, { FC, KeyboardEventHandler, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Combobox, useCombobox, VisuallyHidden } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import { Props as IInfiniteScrollProps } from 'react-infinite-scroll-component';

import type { DropdownPosition } from '../../types/DropdownPosition';
import type { IOption } from '../../types/IOption';
import { EMPTY_DISPLAY_PLACEHOLDER } from '../../theme/forms';
import { usePortalTarget } from '../../provider/UiContext';
import { FieldA11yContext } from '../../context/FieldA11yContext';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import Chip from '../Chip';

import { MdClose, MdOutlineExpandMore } from 'react-icons/md';
import clsx from 'clsx';
import usePrevious from '../../hooks/usePrevious';

import { MenuVariantSize } from './Select.types';
import SelectMenu from './Select.Menu';
import './Select.scss';
import { useDebounce } from '../../hooks/useDebounce';

export interface ISelectProps<T extends HTMLElement = HTMLDivElement> {
    /** Ссылка на поле ввода (combobox) — фокус поля формы (`setFocus`, ошибка валидации) */
    ref?: React.Ref<HTMLInputElement>;
    /** Варианты выбора */
    options: IOption[];
    /** Изменение значения */
    onChange?: (option: IOption[]) => void;
    onSearchChange?: (val: string) => void;
    /** Значение */
    values?: IOption[];
    defInputValue?: string;
    /** Поиск внутри селекта
     * @param query - строка поиска
     * @param isPagination - указывает что изменилась пагинация
     */
    onSearch?: (query: string, isPagination?: boolean) => void;
    /**
     * Множественный выбор
     * @default false
     *  */
    multiselect?: boolean;
    /** Плейсхолдер */
    placeholder?: string;
    /** Доступное имя поля ввода, когда видимой подписи с ним не связано */
    'aria-label'?: string;
    /** id подписи поля — доступное имя из видимого текста; в `FormGroup` — её подпись по умолчанию */
    'aria-labelledby'?: string;
    /** id описания поля; в `FormGroup` к нему добавляются сообщение под полем и пояснение группы */
    'aria-describedby'?: string;
    /**
     *  Запрещает вводить текст (список без поиска) и скрывает крестик сброса
     *  @default false
     */
    readOnly?: boolean;
    /**
     * Поиск по вводу в поле. По умолчанию — авто: поиск есть у длинных списков (больше 7 вариантов), у асинхронных
     * (`isAsync`, `onSearch`, `debouncedInput`) и у свободного ввода (`onSearchChange`); короткий фиксированный
     * список — без поиска. Без поиска поле не редактируется: список открывается щелчком, Enter, пробелом или
     * стрелками, по первым буквам — переход к пункту.
     */
    searchable?: boolean;
    /**
     * залочен или нет
     * @default false
     *  */
    disabled?: boolean;
    /** Максимальное количество выбранных вариантов при multiselect */
    maxOptions?: number | undefined;
    /**
     * Прелоудер при ленивой загрузке
     * @default false
     *  */
    loading?: boolean;
    /**
     * Очистить селект при выборе значения
     * @default false
     * */
    clearOnSelect?: boolean;

    onlySelect?: boolean;
    /** Любое изменяемое значение (зависимость). При изменении этого параметра очищается селект */
    /**
     * Вид селекта
     * @default 'base'
     *  */
    variant?: 'base' | 'tag' | 'menu';
    /**
     * Переводит селект в невалидный статус
     * @default false
     *  */
    invalid?: boolean;
    /** Указывает будет ли селект асинхронным
     * Если значение указано true, тогда нужно передавать infinityScrollProps
     * @default false
     */
    isAsync?: boolean;
    /**
     * Мультиселект без строки поиска: выбранные значения — только теги
     * @default false
     */
    disabledSearch?: boolean;
    /** Пропсы для infinityScroll
     * @requires dataLength текущая длина массива
     * @requires hasMore стоит ли еще загружать данные
     */
    infinityScrollProps?: Omit<IInfiniteScrollProps, 'children' | 'next' | 'scrollableTarget' | 'loader'>;
    /**
     * Расположение
     * @default 'bottom'
     *  */
    position?: DropdownPosition;
    /** Событие скролла для выпадающего списка */
    onScroll?: (e: React.UIEvent) => void;
    startAdornment?: ReactNode | undefined;
    endAdornment?: ReactNode | undefined;
    /** Список по ширине самого длинного пункта (не уже поля), но не шире заданного */
    dropdownMaxWidth?: number | string;
    /**
     * Размер поля: `sm` — компактный (32px) для плотных раскладок (плитки, карточки)
     * @default 'md'
     */
    size?: 'sm' | 'md';
    /** Список открыт сразу при монтировании — редактор ячейки таблицы открывается одним кликом. */
    defaultOpened?: boolean;
    /** Ссылка на контейнер портала; без неё — `portalTarget` из `UiProvider`, иначе body */
    containerRef?: RefObject<T>;
    /**
     * Цвет селекта
     * @default 'white'
     * */
    backgroundColor?: 'white' | 'gray';
    /**
     * Размер кнопки меню
     * @default 'm'
     * */
    menuVariantSize?: MenuVariantSize;

    /**
     * Подсвечивать текст в элементах меню
     * @default true
     * */
    highlightMenuItems?: boolean;

    minCharsToDebounce?: number;
    debounceTime?: number;
    debouncedInput?: (val: string) => void;
    allowEmptyStringDebounce?: boolean;

    testid?: string;

    /**
     *  Отображение иконки сброса значения
     * @default true
     *  */
    showClearIcon?: boolean;
    /**
     * Выравнивание пунктов списка: `start` — по левому краю (подсказки к свободному вводу под набранным текстом), как
     * `listItemAlign` у меню
     * @default 'center'
     */
    listItemAlign?: 'center' | 'start';
}

// Фасад на Mantine Combobox. Публичный ISelectProps и весь DOM триггера
// (обёртка/инпут/чипы-теги/шеврон/сброс, классы rf-select__*) сохранены; заменена
// механика: позиционирование и открытие дропдауна — Combobox (DropdownTarget по обёртке,
// ширина = ширине поля), клавиатура (стрелки/Enter/Escape) — стор Combobox вместо
// самодельного moveFocus по li, дисмисс по клику-вне — Combobox. Само наполнение
// дропдауна (чекбоксы multiselect, подсветка запроса, InfiniteScroll, прелоадер) —
// в Select.Menu на Combobox.Option. Легаси-гейтинг «пустой список не показываем» —
// эффект-синхронизация intent-состояния showDropdown со стором.

// До семи вариантов список читается взглядом (в 250px помещается шесть строк — прокрутка не больше строки) и
// быстро перебирается по первым буквам: поиск в нём лишний, а набранный текст пользователь принимает за значение.
const SEARCH_MIN_OPTIONS = 8;
// Пауза, после которой буквы перехода к пункту набираются заново (как у нативного select).
const TYPEAHEAD_RESET_MS = 500;

type PendingHighlight = 'active' | 'first' | 'last' | number;

const isPrintableKey = (event: React.KeyboardEvent) =>
    event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

// Сообщает о появлении списка: эффекты Combobox.Options (id списка в сторе) к этому моменту отработали,
// пункты уже в DOM.
const ListMountProbe = ({ onMount }: { onMount: () => void }) => {
    // только при появлении списка
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => onMount(), []);
    return null;
};

const Select: FC<ISelectProps> & { id: number } = ({
    ref,
    options,
    onChange: propsOnChange,
    invalid = false,
    onSearch,
    values: propsValues,
    defInputValue = '',
    multiselect = false,
    placeholder = '',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    variant = 'base',
    disabled = false,
    readOnly = variant === 'menu',
    searchable,
    maxOptions = undefined,
    loading = false,
    clearOnSelect = false,
    onlySelect = false,
    disabledSearch = false,
    isAsync,
    infinityScrollProps,
    dropdownMaxWidth,
    // список шире поля выравниваем по левому краю поля, а не по центру
    position = variant === 'menu' ? 'bottom-end' : dropdownMaxWidth ? 'bottom-start' : 'bottom',
    endAdornment,
    startAdornment,
    onScroll,
    size = 'md',
    defaultOpened = false,
    containerRef,
    backgroundColor = 'white',
    menuVariantSize = 'm',
    minCharsToDebounce,
    debounceTime,
    debouncedInput,
    onSearchChange,
    allowEmptyStringDebounce,
    highlightMenuItems = true,
    showClearIcon = true,
    listItemAlign = 'center',
    testid,
}: ISelectProps) => {
    const id = React.useMemo(() => {
        return Select.id++;
    }, []);
    const portalTarget = usePortalTarget();

    const inputRef = useRef<HTMLInputElement>(null);
    const mergedInputRef = useMergedRef(inputRef, ref);
    const fieldA11y = useFieldA11y({
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
    });
    const selectDropDownRef = useRef<HTMLUListElement>(null);

    // Intent-состояние (хочет ли юзер видеть дропдаун); фактическая видимость стора
    // Mantine синхронизируется эффектом ниже с учётом «пустой список не показываем».
    const [showDropdown, setShowDropdown] = useState(defaultOpened);
    const toggleRef = useRef<HTMLDivElement>(null);
    const [isOnMove, setIsOnMove] = useState<boolean>(false);
    const firstElementPosition = useRef<number>(0);
    const prevShowDropDown = usePrevious(showDropdown) || false;

    const [valueState, setValueState] = useState<IOption[]>([]);

    const selectValues = propsValues ?? valueState;

    const [inputValue, setInputValue] = useState<string>(() =>
        selectValues.length > 0 && !multiselect ? selectValues[0].label : defInputValue === null ? '' : defInputValue,
    );

    // Свободный ввод: набранный текст — само значение (автодополнение), его не откатываем.
    const freeInput = onSearchChange !== undefined;
    const isSearchable =
        !readOnly &&
        !disabledSearch &&
        (searchable ??
            (freeInput || !!debouncedInput || !!isAsync || !!onSearch || options.length >= SEARCH_MIN_OPTIONS));
    const selectOnly = !isSearchable;
    // Подсвечивается только набранный текст: подпись выбранного значения в поле — не поиск.
    const searchQuery =
        isSearchable && !(selectValues.length > 0 && !multiselect && inputValue === selectValues[0].label)
            ? inputValue
            : '';

    // Подсветка, которую нужно поставить, когда открываемый с клавиатуры список появится в DOM.
    const pendingHighlight = useRef<PendingHighlight | null>(null);
    // Выбор в открытом списке: подпись выбранного уже в поле, откатывать при закрытии нечего.
    const pickedOnClose = useRef(false);
    const typeahead = useRef({ query: '', time: 0 });
    const [activeOptionId, setActiveOptionId] = useState<string | null>(null);

    const listId = `Select-${id}-list-scroll`;
    // Список прячется сам, когда показывать нечего (поиск без совпадений): намерение «открыт» остаётся,
    // иначе закрытие откатило бы текст посреди набора.
    const hidingEmptyList = useRef(false);
    const combobox = useCombobox({
        onDropdownClose: () => {
            if (!hidingEmptyList.current) setShowDropdown(false);
            combobox.resetSelectedOption();
        },
    });

    useDebounce(inputValue, debounceTime ?? 0, (val) => {
        if ((allowEmptyStringDebounce || val) && val.length >= (minCharsToDebounce ?? 3)) {
            if (showDropdown && debouncedInput) debouncedInput(val);
        }
    });

    const setSelectValues = (next: IOption[]) => {
        // uncontrolled
        if (!propsValues) {
            setValueState(next);
        }

        propsOnChange?.(next);
    };

    /** Очистка селекта */
    const onClear = () => {
        setInputValue('');
        setFilteredOptions(options);
        if (!multiselect) {
            setSelectValues([
                {
                    label: '',
                    value: '',
                },
            ]);

            if (debouncedInput) debouncedInput('');
        }

        onSearch && onSearch('');
    };

    const onClearSelectedValues = () => {
        setSelectValues([
            {
                label: '',
                value: '',
            },
        ]);
    };

    const onOpen = useCallback(
        (keepInputFocus: boolean = true) => {
            if (!disabled) {
                setShowDropdown(true);

                if (keepInputFocus) {
                    setTimeout(() => {
                        inputRef.current?.focus();
                    }, 30);
                }
            }
        },
        [disabled],
    );

    const handleInputClick = (event: React.MouseEvent) => {
        // щелчок в строке поиска ставит каретку, а не закрывает список: закрытие откатило бы набранный текст
        if (isSearchable && event.target === inputRef.current) {
            // открытие выделяет подпись значения: набор заменяет её, а не дописывается в конец
            if (!showDropdown && !multiselect) inputRef.current.select();
            setShowDropdown(true);
            return;
        }
        setShowDropdown((state) => !state);
    };

    const onReset = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClear();
        onClearSelectedValues();
        onOpen();
    };

    // -------------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        setInputValue(selectValues.length > 0 && !multiselect ? selectValues[0].label : defInputValue);
    }, [selectValues]);

    // -------------------------------------------------------------------------------------------------------------------

    /** Поиск в селекте */
    const onSelectSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const query = val.toLowerCase();
        setInputValue(val);
        setFilteredOptions(query ? options.filter((o: IOption) => o.label.toLowerCase().includes(query)) : options);
    };

    // без поиска ввод не меняет поле: управляемый input вернёт прежнюю подпись (вставка, IME, автозаполнение)
    const ignoreInput = () => {};

    // -------------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (clearOnSelect) {
            setInputValue('');
        }

        if (multiselect && selectValues.length === 1 && toggleRef.current) {
            toggleRef.current.getBoundingClientRect().y = firstElementPosition.current;
        }
    }, [multiselect, selectValues]);

    const onValueChange = (option: IOption) => {
        let result = undefined;

        if (multiselect) {
            const index = selectValues.findIndex((o: IOption) => option.value === o.value);

            if (index >= 0) {
                result = selectValues.filter((_: IOption, i: number) => i !== index);
            } else {
                if (typeof maxOptions === 'number' ? selectValues.length < maxOptions : true) {
                    result = [...selectValues, option];
                }
            }
        } else {
            result = [option];
        }

        if (result) {
            setSelectValues(result);
        }
    };

    // -------------------------------------------------------------------------------------------------------------------

    const [filteredOptions, setFilteredOptions] = useState<IOption[]>([]);

    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    // -------------------------------------------------------------------------------------------------------------------

    const handleOptionClick = async (option: IOption) => {
        onValueChange(option);

        if (!multiselect) {
            pickedOnClose.current = true;
            setInputValue(clearOnSelect ? '' : option.label);
            setShowDropdown(false);
            inputRef.current?.focus();
        } else {
            setInputValue('');
        }
    };

    /** Сабмит опции из стора Combobox: value — индекс в filteredOptions. */
    const handleOptionSubmit = (value: string) => {
        const option = filteredOptions[Number(value)];
        if (option) void handleOptionClick(option);
    };

    // Синхронизация intent → стор Mantine: дропдаун виден, только если есть что
    // показывать (опции или прелоадер) — легаси-гейтинг пустого списка.
    const dropdownVisible = showDropdown && (!!filteredOptions.length || loading);
    useEffect(() => {
        if (dropdownVisible) {
            combobox.openDropdown();
        } else {
            hidingEmptyList.current = true;
            combobox.closeDropdown();
            hidingEmptyList.current = false;
        }
    }, [dropdownVisible]);

    // Список закрыли без выбора (Escape, щелчок мимо, Tab, уход фокуса) — набранный текст не значение: в поле
    // возвращается подпись текущего значения, у мультиселекта — пусто.
    useEffect(() => {
        const picked = pickedOnClose.current;
        pickedOnClose.current = false;
        if (showDropdown || !prevShowDropDown) return;
        pendingHighlight.current = null;
        setActiveOptionId(null);
        if (picked || freeInput) return;
        const valueText = selectValues.length > 0 && !multiselect ? selectValues[0].label : (defInputValue ?? '');
        setInputValue(clearOnSelect ? '' : valueText);
        setFilteredOptions(options);
        // только при закрытии списка
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDropdown]);

    // selectOption стора id пункта не отдаёт (в типах — void), а для aria-activedescendant он нужен
    const selectOptionAt = (index: number) => {
        combobox.selectOption(index);
        const list = (inputRef.current?.ownerDocument ?? document).getElementById(listId);
        const option = list?.querySelectorAll<HTMLElement>('[data-combobox-option]')[index];
        return option?.hasAttribute('data-combobox-selected') ? option.id : null;
    };

    const highlight = (target: PendingHighlight) => {
        let index = target;
        if (target === 'active') {
            const current = multiselect ? -1 : filteredOptions.findIndex((o) => o.value === selectValues[0]?.value);
            index = current >= 0 && !filteredOptions[current].disabled ? current : 'first';
        }
        if (index === 'first') {
            setActiveOptionId(combobox.selectFirstOption());
            return;
        }
        if (index === 'last') {
            let last = filteredOptions.length - 1;
            while (last >= 0 && filteredOptions[last].disabled) last--;
            if (last < 0) return;
            index = last;
        }
        setActiveOptionId(selectOptionAt(index as number));
    };

    const onListMount = () => {
        const target = pendingHighlight.current;
        pendingHighlight.current = null;
        if (target !== null) {
            // после расчёта позиции списка: подсветка прокручивает его к пункту (scrollIntoView), а список,
            // ещё не прижатый к полю, потянул бы за собой прокрутку страницы
            window.setTimeout(() => highlight(target), 0);
        } else if (selectOnly) {
            // открыли мышью: стрелки ведут от текущего значения, подсветки нет — вид списка прежний
            combobox.updateSelectedOptionIndex('active');
        }
    };

    const openWithHighlight = (target: PendingHighlight) => {
        if (disabled) return;
        if (combobox.dropdownOpened) {
            highlight(target);
            return;
        }
        pendingHighlight.current = target;
        setShowDropdown(true);
    };

    /** Переход по первым буквам: повтор одной буквы перебирает пункты на неё, быстрый набор ищет по началу. */
    const findTypeaheadIndex = (char: string) => {
        const now = Date.now();
        const state = typeahead.current;
        const query = (now - state.time > TYPEAHEAD_RESET_MS ? char : state.query + char).toLowerCase();
        state.query = query;
        state.time = now;

        const repeated = query.split('').every((c) => c === query[0]);
        const needle = repeated ? query[0] : query;
        const highlighted = combobox.dropdownOpened ? combobox.getSelectedOptionIndex() : -1;
        const current =
            highlighted >= 0 ? highlighted : filteredOptions.findIndex((o) => o.value === selectValues[0]?.value);
        // новую букву ищем со следующего пункта, продолжение слова — с текущего
        const start = current + (repeated || query.length === 1 ? 1 : 0);
        for (let i = 0; i < filteredOptions.length; i++) {
            const index = (start + i + filteredOptions.length) % filteredOptions.length;
            const option = filteredOptions[index];
            const label = String(option.label ?? '').toLowerCase();
            if (!option.disabled && label.startsWith(needle)) return index;
        }
        return -1;
    };

    // Список без поиска — combobox «только выбор» (WAI-ARIA APG, select-only combobox).
    const handleSelectOnlyKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const opened = combobox.dropdownOpened;
        const typing = typeahead.current.query !== '' && Date.now() - typeahead.current.time <= TYPEAHEAD_RESET_MS;
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                event.preventDefault();
                if (!opened) openWithHighlight('active');
                else
                    setActiveOptionId(
                        event.key === 'ArrowDown' ? combobox.selectNextOption() : combobox.selectPreviousOption(),
                    );
                return;
            case 'Home':
            case 'End':
                event.preventDefault();
                openWithHighlight(event.key === 'Home' ? 'first' : 'last');
                return;
            case 'Enter':
                event.preventDefault();
                if (!opened) openWithHighlight('active');
                else if (combobox.getSelectedOptionIndex() >= 0) combobox.clickSelectedOption();
                else setShowDropdown(false);
                return;
            case 'Escape':
                if (showDropdown) {
                    // окно вокруг не закрывается: Escape адресован списку
                    event.preventDefault();
                    setShowDropdown(false);
                }
                return;
        }
        if (event.key === ' ' && !typing) {
            event.preventDefault();
            if (!opened) openWithHighlight('active');
            else if (combobox.getSelectedOptionIndex() >= 0) combobox.clickSelectedOption();
            return;
        }
        if (isPrintableKey(event)) {
            event.preventDefault();
            const index = findTypeaheadIndex(event.key);
            if (index >= 0) openWithHighlight(index);
        }
    };

    // -------------------------------------------------------------------------------------------------------------------

    // открытый при монтировании список ведётся с клавиатуры (стрелки/Enter) — фокус в поле
    useEffect(() => {
        const input = inputRef.current;
        if (!defaultOpened || !input) return;
        input.focus({ preventScroll: true });
        // каретка в конце прокручивает длинное значение узкого поля — показываем начало; в строке поиска подпись
        // выделена целиком, чтобы набор заменял её
        if (isSearchable) input.setSelectionRange(0, input.value.length, 'backward');
        else input.setSelectionRange(0, 0);
        input.scrollLeft = 0;
        // только при монтировании
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const noop = () => {};

    // Шеврон справа — у всех вариантов, у которых ЕСТЬ что раскрывать. При пустых
    // options (автокомплит до ввода) кнопка не рендерится вовсе — иначе у поля
    // остаётся пустой слот под невидимую иконку, а крестик сброса висит левее него.
    const hasChevron = variant !== 'tag' && options.length > 0;

    const inputVariantClass =
        variant === 'menu' ? `rf-select__menu${disabled ? '--disabled' : ''} rf-select__menu--${menuVariantSize}` : '';
    const inputMultiselectClass = multiselect && selectValues.length ? 'rf-select__input--multiselect' : '';
    const inputOnMoveClass = isOnMove && variant === 'menu' ? 'rf-button__hover' : '';
    // запас справа под две кнопки (крестик + шеврон) в одиночном режиме с текстом;
    // без шеврона хватает базового padding-right инпута (одна кнопка)
    const showClose = Boolean(!disabled && !readOnly && inputValue && inputValue.length > 0 && !onlySelect);
    const showCloseForPadding = showClearIcon && showClose;
    const inputClearableClass =
        showCloseForPadding && hasChevron && !(multiselect && selectValues.length) ? 'rf-select__input--clearable' : '';

    const inputClass = `rf-select__input ${inputMultiselectClass} ${inputVariantClass} ${inputOnMoveClass} ${inputClearableClass}`;

    // Приглашающий плейсхолдер только в интерактивном пустом одиночном поле. С тегами
    // мультиселекта — пусто (их рендерит tagsJSX); в disabled — прочерк вместо приглашения.
    const basePlaceholder = multiselect && selectValues.length ? '' : options.length ? placeholder : '';
    const selectPlaceholder = disabled && basePlaceholder ? EMPTY_DISPLAY_PLACEHOLDER : basePlaceholder;

    const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        if (event.key === 'Tab') {
            // фокус уходит — список закрывается, а не остаётся открытым у чужого поля
            if (showDropdown) setShowDropdown(false);
            return;
        }
        if (selectOnly) {
            handleSelectOnlyKeyDown(event);
            return;
        }
        if (event.key === 'Escape') {
            if (showDropdown) {
                event.preventDefault();
                setShowDropdown(false);
            }
            return;
        }
        // Навигацию по опциям (стрелки/Enter/Escape) обрабатывает стор Combobox
        // (EventsTarget); здесь — только легаси-поведение «любая клавиша открывает».
        onOpen();
    };

    const onInputBlur = () => {
        if (readOnly == undefined || readOnly == false) {
            if (onSearchChange) {
                onSearchChange(inputValue);
            }
        }
        // список скрыт (совпадений нет) — щелчок мимо до Combobox не дойдёт, закрываем сами, чтобы откатить текст
        if (showDropdown && !combobox.dropdownOpened) setShowDropdown(false);
    };

    const expanded = combobox.dropdownOpened;
    // У списка без поиска ARIA и клавиатура свои (APG select-only combobox): Mantine ведёт их как у строки поиска.
    const selectOnlyProps = selectOnly
        ? {
              role: 'combobox',
              'aria-haspopup': 'listbox' as const,
              'aria-expanded': expanded,
              'aria-controls': expanded ? listId : undefined,
              'aria-activedescendant': (expanded && activeOptionId) || undefined,
              'data-mantine-stop-propagation': expanded || undefined,
              // экранная клавиатура телефона не нужна: вводить нечего
              inputMode: 'none' as const,
          }
        : {};

    const renderInput = (dataTestId: string | undefined) => (
        <Combobox.EventsTarget
            withKeyboardNavigation={isSearchable}
            withAriaAttributes={isSearchable}
            // строка поиска — тоже combobox (APG editable combobox): роль и aria-expanded
            withExpandedAttribute={isSearchable}
        >
            <input
                ref={mergedInputRef}
                autoSave="false"
                autoComplete="off"
                data-testid={dataTestId}
                aria-label={ariaLabel}
                {...fieldA11y}
                aria-invalid={invalid || undefined}
                aria-autocomplete={isSearchable ? 'list' : undefined}
                {...selectOnlyProps}
                className={clsx(inputClass, selectOnly && 'rf-select__input--select-only')}
                onChange={isSearchable ? onSelectSearch : ignoreInput}
                onKeyDown={handleInputKeyDown}
                onBlur={onInputBlur}
                value={inputValue}
                disabled={disabled}
                placeholder={selectPlaceholder}
            />
        </Combobox.EventsTarget>
    );
    const inputElement = renderInput(testid ? `simpleselect-${testid}` : 'rf-select');

    const tagsRef = useRef<HTMLDivElement>(null);

    const tagsJSX = multiselect && selectValues.length > 0 && (
        <div
            className={disabledSearch ? 'rf-select-without-search__tags' : 'rf-select__tags'}
            onClick={(event) => {
                // всплыв до обёртки её переключатель тут же закрыл бы открываемый список
                event.stopPropagation();
                if (!disabled) onOpen(true);
            }}
        >
            {selectValues.map((t: IOption, index: number) => (
                <div ref={tagsRef} className={clsx('rf-select__tag')} key={index}>
                    <Chip
                        variant="secondary"
                        size="xs"
                        onRemove={disabled ? undefined : () => onValueChange(t)}
                        removeLabel={`Удалить «${t.label}»`}
                        onClick={noop}
                        disabled={disabled}
                    >
                        {t.label}
                    </Chip>
                </div>
            ))}
            {disabledSearch ? (
                // Строки поиска нет, но поле остаётся — невидимым: в нём фокус формы, имя и сообщение поля, клавиатура
                // списка (combobox «только выбор»). Без якоря строки поиска: его у такого поля нет
                <VisuallyHidden>{renderInput(undefined)}</VisuallyHidden>
            ) : (
                <div className={clsx('rf-select__input--multiselect')}>{inputElement}</div>
            )}
        </div>
    );

    // -------------------------------------------------------------------------------------------------------------------

    const closeButton = showClose && (
        <button
            type="button"
            // одиночный режим: крестик левее шеврона, а без шеврона (пустые options) —
            // на его месте; multiselect сохраняет свою позицию (right:48, top:8 — под
            // раскладку тегов)
            className={
                multiselect && selectValues.length
                    ? 'rf-select__button--multiselect'
                    : clsx('rf-select__button', hasChevron && 'rf-select__button--clear')
            }
            onClick={onReset}
            aria-label="Сбросить"
        >
            <MdClose />
        </button>
    );

    const onChevronClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDropdown((state: boolean) => !state);
    };

    const onMouseMoveHandler = () => setIsOnMove(true);

    const onMouseLeaveHandler = () => setIsOnMove(false);

    useEffect(() => {
        if (!showDropdown && showDropdown !== prevShowDropDown) {
            if (!filteredOptions.length || !options.find((opt) => opt.label === inputValue)) {
                if (clearOnSelect) {
                    onClear();
                }
            }
        }
    }, [prevShowDropDown, filteredOptions, options]);

    const chevronButton = hasChevron && (
        <div
            data-testid="rf-select__chevron"
            className={clsx(
                multiselect && selectValues.length ? 'rf-select__button-multiselect-chevron' : 'rf-select__button',
                showDropdown && 'rf-select__button--rotate',
                variant === 'menu' ? 'rf-select__button--menu' : '',
                isOnMove && variant === 'menu' ? 'rf-button__hover' : '',
            )}
            onClick={onChevronClick}
            onMouseMove={onMouseMoveHandler}
            aria-label={showDropdown ? 'Скрыть меню' : 'Раскрыть меню'}
        >
            <MdOutlineExpandMore size="20" color={variant === 'menu' ? 'var(--text-on-accent)' : ''} />
        </div>
    );

    const startAdornmentIcon =
        startAdornment && variant !== 'tag' ? <div className="rf-select__button__icon">{startAdornment}</div> : null;
    const endAdornmentIcon =
        endAdornment && variant !== 'tag' ? <div className="rf-select__button__icon--end">{endAdornment}</div> : null;

    // -------------------------------------------------------------------------------------------------------------------

    // Список поиска без совпадений скрыт (легаси: пустой список не показываем) — диктору говорим, что пусто.
    // Только у локального поиска: асинхронный список в это время ещё грузится.
    const localSearch = isSearchable && !isAsync && !onSearch && !debouncedInput && !freeInput;
    const emptyText = localSearch && showDropdown && !filteredOptions.length && !loading && !!searchQuery;

    const isTagVariant = variant === 'tag';
    const portalHost = containerRef?.current ?? portalTarget;
    const openClass = showDropdown ? 'rf-select__wrapper--open' : '';
    const tagClass = isTagVariant ? 'rf-select__wrapper--tag' : '';

    return (
        <div
            className={clsx(
                'rf-select',
                tagClass,
                backgroundColor === 'gray' && 'rf-select__background-gray',
                size === 'sm' && 'rf-select--sm',
            )}
            ref={toggleRef}
        >
            <Combobox
                store={combobox}
                withinPortal
                // легаси-паритет: закрытый дропдаун размонтирован (тесты ждут отсутствия в DOM)
                keepMounted={false}
                portalProps={portalHost ? { target: portalHost } : undefined}
                position={position}
                offset={8}
                zIndex={1000}
                // fixed: открытый список у нижнего края окна не удлиняет страницу (иначе появлялась
                // прокрутка страницы и окно становилось уже)
                floatingStrategy="fixed"
                // ширина = ширине поля (легаси: maxWidth по toggleRef); dropdownMaxWidth —
                // список по содержимому. Явное значение обязательно: undefined Mantine
                // заменяет дефолтом Combobox 'target', и size-middleware инлайном ставит
                // ширину поля поверх style дропдауна
                width={isTagVariant ? undefined : dropdownMaxWidth ? 'max-content' : 'target'}
                onOptionSubmit={handleOptionSubmit}
            >
                <Combobox.DropdownTarget>
                    <div
                        onMouseLeave={onMouseLeaveHandler}
                        data-testid={testid ? `select-${testid}` : undefined}
                        className={clsx({
                            'rf-select__wrapper': true,
                            'rf-select__wrapper--multiselect': multiselect,
                            'rf-select__wrapper--invalid': invalid,
                            [openClass]: true,
                            'rf-select__wrapper--disabled': disabled,
                            'rf-select__wrapper--menu': variant === 'menu',
                        })}
                        onClick={handleInputClick}
                    >
                        {startAdornmentIcon}
                        <div
                            className={clsx(
                                multiselect && selectValues.length
                                    ? 'rf-select__wrapper--input'
                                    : 'rf-select-input__container',
                                !showDropdown && multiselect && selectValues.length
                                    ? 'rf-select--multiselect--fixed'
                                    : '',
                            )}
                        >
                            {tagsJSX}
                            {multiselect ? (selectValues.length < 1 ? inputElement : null) : inputElement}
                        </div>
                        {endAdornmentIcon}
                        {showClearIcon && closeButton}
                        {chevronButton}
                    </div>
                </Combobox.DropdownTarget>

                <Combobox.Dropdown
                    className={clsx(
                        'rf-select__dropdown rf-dropdown__content',
                        size === 'sm' && 'rf-select__dropdown--sm',
                    )}
                    data-testid="rf-dropdown"
                    data-position={position}
                    style={{
                        maxWidth: isTagVariant ? undefined : dropdownMaxWidth,
                        minWidth: !isTagVariant && dropdownMaxWidth ? toggleRef.current?.offsetWidth : undefined,
                        width: isTagVariant ? 'auto' : undefined,
                    }}
                >
                    {/* пункты списка — не поле группы: флажки мультиселекта не берут её подпись и ошибку */}
                    <FieldA11yContext value={null}>
                        <SelectMenu
                            ref={selectDropDownRef}
                            options={filteredOptions}
                            selected={selectValues}
                            multiselect={multiselect}
                            selectId={id}
                            menuVariantSize={menuVariantSize}
                            isAsync={isAsync}
                            infinityScrollProps={infinityScrollProps}
                            query={highlightMenuItems ? searchQuery : ''}
                            loading={loading}
                            onSearch={onSearch}
                            onScroll={onScroll}
                            listItemAlign={listItemAlign}
                        />
                    </FieldA11yContext>
                    <ListMountProbe onMount={onListMount} />
                </Combobox.Dropdown>
            </Combobox>
            {localSearch && <VisuallyHidden role="status">{emptyText ? 'Ничего не найдено' : ''}</VisuallyHidden>}
        </div>
    );
};

Select.id = 0;

export default Select;
